import { type FC, Suspense, useEffect } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { Link, useParams } from "react-router";
import { useCompany } from "../company/useCompany";
import { Stepper } from "../components/Stepper";
import { LinkGen } from "../LinkGen";
import { AppPage } from "../layout/AppLayout";
import { StepTikTokAdGroup } from "./StepTikTokAdGroup";
import { StepTikTokCampaign } from "./StepTikTokCampaign";
import { StepTikTokCreative } from "./StepTikTokCreative";
import { StepTikTokLeadForm } from "./StepTikTokLeadForm";
import { StepTikTokReview } from "./StepTikTokReview";
import {
	TikTokCampaignWizardProvider,
	useTikTokCampaignWizard,
} from "./TikTokCampaignWizardContext";
import { useTikTokAdsDraft } from "./useTikTokCampaigns";

const STEP_COMPONENTS: Record<string, FC> = {
	Campaign: StepTikTokCampaign,
	"Ad Group": StepTikTokAdGroup,
	"Lead Form": StepTikTokLeadForm,
	Creative: StepTikTokCreative,
	Review: StepTikTokReview,
};

const WizardContent: FC = () => {
	const { currentStep, steps, isDirty } = useTikTokCampaignWizard();
	const StepComponent = STEP_COMPONENTS[steps[currentStep].title];

	useEffect(() => {
		const handler = (event: BeforeUnloadEvent) => {
			if (isDirty) {
				event.preventDefault();
			}
		};
		window.addEventListener("beforeunload", handler);
		return () => window.removeEventListener("beforeunload", handler);
	}, [isDirty]);

	return (
		<div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
			<Stepper steps={steps} current={currentStep} />
			{StepComponent && <StepComponent />}
		</div>
	);
};

const DraftEditLoadError: FC = () => (
	<AppPage title="Edit TikTok Campaign">
		<p className="text-muted">
			Unable to load this draft. It may have been deleted.
		</p>
		<p style={{ marginBottom: 0 }}>
			<Link to={LinkGen.TikTokAds.Index()}>Back to TikTok Ads</Link>
		</p>
	</AppPage>
);

const CampaignDraftEditPage: FC<{ draftId: string }> = ({ draftId }) => {
	const company = useCompany();
	const { data: draft } = useTikTokAdsDraft(draftId);

	return (
		<AppPage
			title="Edit TikTok Campaign"
			subtitle="Update this draft, then save or publish. Campaigns are created paused."
		>
			<TikTokCampaignWizardProvider
				key={draft.id}
				initialOverrides={draft}
				companyName={company.name}
			>
				<WizardContent />
			</TikTokCampaignWizardProvider>
		</AppPage>
	);
};

export const PageTikTokCampaignCreate: FC = () => {
	const company = useCompany();
	const { draftId } = useParams<{ draftId?: string }>();

	if (draftId) {
		return (
			<ErrorBoundary fallback={<DraftEditLoadError />}>
				<Suspense
					fallback={
						<AppPage title="Edit TikTok Campaign">
							<p>Loading draft...</p>
						</AppPage>
					}
				>
					<CampaignDraftEditPage draftId={draftId} />
				</Suspense>
			</ErrorBoundary>
		);
	}

	return (
		<AppPage
			title="Create TikTok Campaign"
			subtitle="Campaigns are created paused so you can review them before they spend"
		>
			<TikTokCampaignWizardProvider companyName={company.name}>
				<WizardContent />
			</TikTokCampaignWizardProvider>
		</AppPage>
	);
};
